import { ClimateLeafLogo } from "@/components/ClimateLeafLogo";
import { Check } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface CarbonOffsetCalloutProps {
  amount: number;
  currencySymbol: string;
  enabled: boolean;
  onToggle: () => void;
  onLearnMore: () => void;
}

/**
 * Branded carbon-removal callout with an inline Add / Added pill.
 *
 * Renders the callout that appears in the checkout summary when
 * `quote.climateOffsetAmount > 0`. Clicking the pill toggles the contribution
 * via `onToggle`; the parent owns the boolean state so it can be applied to
 * the displayed total and to the booking-request body.
 */
export function CarbonOffsetCallout({
  amount,
  currencySymbol,
  enabled,
  onToggle,
  onLearnMore,
}: CarbonOffsetCalloutProps) {
  const { t } = useTranslation();
  const formatted = `${currencySymbol}${amount.toFixed(2)}`;
  return (
    <section
      data-testid="carbon-offset-callout"
      className="rounded-xl border border-border bg-white p-4 dark:bg-card"
    >
      <div className="flex items-center gap-3">
        <ClimateLeafLogo size={64} className="rounded-xl bg-white" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            {t("checkout.carbon_add_title", { amount: formatted })}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <Trans
              i18nKey="checkout.carbon_body"
              values={{ amount: formatted }}
              components={{
                cta: (
                  <button
                    type="button"
                    onClick={onLearnMore}
                    className="font-medium underline-offset-2 hover:underline"
                    style={{ color: "var(--color-brand-light)" }}
                  />
                ),
              }}
            />
          </p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={enabled}
          aria-label={
            enabled
              ? t("checkout.carbon_added_aria")
              : t("checkout.carbon_add_aria", { amount: formatted })
          }
          data-testid="carbon-offset-pill"
          className={cn(
        "cursor-pointer inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
            enabled
              ? "bg-foreground text-background"
              : "border border-foreground bg-white text-foreground hover:bg-muted dark:bg-transparent",
          )}
        >
          {enabled ? (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              {t("checkout.added")}
            </>
          ) : (
            <>{t("checkout.add_carbon_offset")}</>
          )}
        </button>
      </div>
    </section>
  );
}

export default CarbonOffsetCallout;
